import { useAuth } from "@/_core/hooks/useAuth";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/useMobile";
import { trpc } from "@/lib/trpc";
import { 
  Users, Plus, Trash2, Mail, Loader2, Shield, ShieldCheck, ShieldAlert,
  CheckCircle, XCircle, GraduationCap, User
} from "lucide-react";
import { CSSProperties, useEffect, useState } from "react";
import { Link } from "wouter";
import { DashboardLayoutSkeleton } from "@/components/DashboardLayoutSkeleton";
import { AdminSidebar, SIDEBAR_WIDTH_KEY, DEFAULT_WIDTH } from "@/components/AdminSidebar";
import { toast } from "sonner";
import { format, formatDistanceToNow, differenceInDays } from "date-fns";
import { de } from "date-fns/locale";
import { trackEvent, EVENTS } from "@/lib/tracking";

// Role configuration with labels and icons
const ROLE_CONFIG = {
  user: { label: "Benutzer", icon: User, iconColor: "text-gray-500", bgColor: "bg-gray-100", textColor: "text-gray-700", description: "Kann Kurse buchen" },
  instructor: { label: "Kursleiter", icon: GraduationCap, iconColor: "text-green-600", bgColor: "bg-green-50", textColor: "text-green-700", description: "Kann zugewiesene Kurse sehen und Anwesenheit erfassen" },
  courseadmin: { label: "Kursadmin", icon: Shield, iconColor: "text-blue-600", bgColor: "bg-blue-50", textColor: "text-blue-700", description: "Kann Kurse und Kursleiter verwalten" },
  admin: { label: "Admin", icon: ShieldAlert, iconColor: "text-purple-600", bgColor: "bg-purple-50", textColor: "text-purple-700", description: "Vollzugriff auf alle Funktionen" },
} as const;

type RoleKey = keyof typeof ROLE_CONFIG;

// Helper to parse roles string into array
function parseRoles(rolesStr: string): RoleKey[] {
  return rolesStr.split(',').map(r => r.trim()).filter(r => r in ROLE_CONFIG) as RoleKey[];
}

// Helper to convert roles array to string
function rolesToString(roles: RoleKey[]): string {
  // Validation ensures roles.length > 0, so this should never return empty
  return roles.join(',');
}

// Get the primary (highest) role for display
function getPrimaryRole(rolesStr: string): RoleKey {
  const roles = parseRoles(rolesStr);
  if (roles.includes('admin')) return 'admin';
  if (roles.includes('courseadmin')) return 'courseadmin';
  if (roles.includes('instructor')) return 'instructor';
  return 'user';
}

// Get roles for display - hide 'user' if higher roles exist
function getDisplayRoles(rolesStr: string): RoleKey[] {
  const roles = parseRoles(rolesStr);
  // If user has any role other than 'user', don't show 'user' badge
  const higherRoles = roles.filter(r => r !== 'user');
  return higherRoles.length > 0 ? higherRoles : ['user'];
}

type UserFormData = {
  email: string;
  name: string;
  roles: RoleKey[];
  sendInvite: boolean;
};

const defaultFormData: UserFormData = {
  email: "",
  name: "",
  roles: [], // Start with no roles - admin must explicitly select
  sendInvite: true,
};

export default function AdminUsers() {
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });
  const { loading, user } = useAuth();

  useEffect(() => {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, sidebarWidth.toString());
  }, [sidebarWidth]);

  if (loading) return <DashboardLayoutSkeleton />;
  
  // Only admin can access this page
  const userRoles = user?.roles ? parseRoles(user.roles) : [];
  if (!user || !userRoles.includes("admin")) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-8 p-8 max-w-md w-full">
          <ShieldAlert className="h-16 w-16 text-muted-foreground" />
          <h1 className="text-2xl font-semibold">Zugriff verweigert</h1>
          <p className="text-muted-foreground text-center">
            Sie benötigen Admin-Rechte, um diese Seite anzuzeigen.
          </p>
          <Link href="/admin"><Button>Zurück zum Dashboard</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider style={{ "--sidebar-width": `${sidebarWidth}px` } as CSSProperties}>
      <AdminUsersContent setSidebarWidth={setSidebarWidth} />
    </SidebarProvider>
  );
}

function AdminUsersContent({ setSidebarWidth }: { setSidebarWidth: (width: number) => void }) {
  const { user } = useAuth();
  const isMobile = useIsMobile();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState<UserFormData>(defaultFormData);

  const utils = trpc.useUtils();
  const { data: users, isLoading } = trpc.user.list.useQuery();

  const createMutation = trpc.user.create.useMutation({
    onSuccess: (result) => {
      // Track user invitation
      trackEvent(EVENTS.USER_INVITED, { user_id: result.id, user_email: formData.email });
      toast.success("Benutzer erstellt");
      utils.user.list.invalidate();
      setDialogOpen(false);
      setFormData(defaultFormData);
    },
    onError: (error) => toast.error(error.message),
  });

  const updateRolesMutation = trpc.user.updateRoles.useMutation({
    onSuccess: () => {
      toast.success("Rollen aktualisiert");
      utils.user.list.invalidate();
    },
    onError: (error) => toast.error(error.message),
  });

  const toggleRegistrationMutation = trpc.user.toggleRegistration.useMutation({
    onSuccess: () => {
      toast.success("Status aktualisiert");
      utils.user.list.invalidate();
    },
    onError: (error) => toast.error(error.message),
  });

  const deleteMutation = trpc.user.delete.useMutation({
    onSuccess: () => {
      toast.success("Benutzer gelöscht");
      utils.user.list.invalidate();
    },
    onError: (error) => toast.error(error.message),
  });

  const resendInviteMutation = trpc.user.resendInvite.useMutation({
    onSuccess: () => {
      toast.success("Einladung erneut gesendet");
      utils.user.list.invalidate();
    },
    onError: (error) => toast.error(error.message),
  });

  // Helper to check if invitation is expired (7 days)
  const isInvitationExpired = (invitedAt: string | null) => {
    if (!invitedAt) return false;
    const daysSinceInvite = differenceInDays(new Date(), new Date(invitedAt));
    return daysSinceInvite >= 7;
  };

  // Helper to format invitation age
  const getInvitationAge = (invitedAt: string | null) => {
    if (!invitedAt) return null;
    return formatDistanceToNow(new Date(invitedAt), { addSuffix: true, locale: de });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate at least one role is selected
    if (formData.roles.length === 0) {
      toast.error("Bitte wählen Sie mindestens eine Rolle aus");
      return;
    }
    
    createMutation.mutate({
      email: formData.email,
      name: formData.name || undefined,
      roles: rolesToString(formData.roles),
      sendInvite: formData.sendInvite,
    });
  };

  const openCreateDialog = () => {
    setFormData(defaultFormData);
    setDialogOpen(true);
  };

  const toggleFormRole = (role: RoleKey) => {
    setFormData(prev => {
      const newRoles = prev.roles.includes(role)
        ? prev.roles.filter(r => r !== role)
        : [...prev.roles, role];
      // Allow empty selection - validation will catch it on submit
      return { ...prev, roles: newRoles as RoleKey[] };
    });
  };

  const handleRoleToggle = (userId: number, currentRoles: string, role: RoleKey) => {
    const roles = parseRoles(currentRoles);
    const newRoles = roles.includes(role)
      ? roles.filter(r => r !== role)
      : [...roles, role];
    // Ensure at least 'user' role
    const finalRoles = (newRoles.length > 0 ? newRoles : ['user']) as RoleKey[];
    updateRolesMutation.mutate({ id: userId, roles: rolesToString(finalRoles) });
  };

  // Check if current user can modify target user
  const canModifyUser = (targetRoles: string) => {
    const currentUserRoles = user?.roles ? parseRoles(user.roles) : [];
    if (currentUserRoles.includes("admin")) return true;
    const targetRolesList = parseRoles(targetRoles);
    if (targetRolesList.includes("admin")) return false;
    return true;
  };

  return (
    <>
      <AdminSidebar setSidebarWidth={setSidebarWidth} />

      <SidebarInset>
        {isMobile && (
          <div className="flex border-b h-14 items-center justify-between bg-background/95 px-2 backdrop-blur sticky top-0 z-40">
            <div className="flex items-center gap-2">
              <SidebarTrigger className="h-9 w-9 rounded-lg bg-background" />
              <span className="tracking-tight text-foreground font-medium">Benutzer</span>
            </div>
          </div>
        )}
        <main className="flex-1 p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Benutzerverwaltung</h1>
              <p className="text-muted-foreground">Verwalten Sie Benutzer und deren Zugriffsrechte</p>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={openCreateDialog} className="gap-2 bg-primary hover:bg-primary/90 w-full sm:w-auto">
                  <Plus className="h-4 w-4" />
                  Benutzer einladen
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Neuen Benutzer einladen</DialogTitle>
                  <DialogDescription>
                    Erstellen Sie einen neuen Benutzer, der sich dann mit seiner E-Mail-Adresse anmelden kann.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">E-Mail-Adresse *</Label>
                    <Input 
                      id="email" 
                      type="email" 
                      required 
                      value={formData.email} 
                      onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} 
                      placeholder="benutzer@beispiel.de" 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="name">Name (optional)</Label>
                    <Input 
                      id="name" 
                      value={formData.name} 
                      onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} 
                      placeholder="Max Mustermann" 
                    />
                  </div>
                  <div className="space-y-3">
                    <Label>Rollen</Label>
                    <div className="space-y-2">
                      {(Object.keys(ROLE_CONFIG) as RoleKey[]).filter(r => r !== 'user').map(role => {
                        const config = ROLE_CONFIG[role];
                        const Icon = config.icon;
                        return (
                          <div key={role} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-muted/50">
                            <Checkbox
                              id={`role-${role}`}
                              checked={formData.roles.includes(role)}
                              onCheckedChange={() => toggleFormRole(role)}
                            />
                            <label htmlFor={`role-${role}`} className="flex items-center gap-2 cursor-pointer flex-1">
                              <Icon className={`h-4 w-4 ${config.iconColor}`} />
                              <div>
                                <span className="font-medium">{config.label}</span>
                                <p className="text-xs text-muted-foreground">{config.description}</p>
                              </div>
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  <div className="border-t pt-4 mt-2">
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id="sendInvite"
                        checked={formData.sendInvite}
                        onCheckedChange={(checked) => setFormData(p => ({ ...p, sendInvite: !!checked }))}
                      />
                      <label htmlFor="sendInvite" className="flex items-center gap-2 cursor-pointer flex-1">
                        <Mail className="h-4 w-4 text-primary" />
                        <div>
                          <span className="font-medium">Einladungs-E-Mail senden</span>
                          <p className="text-xs text-muted-foreground">Der Benutzer erhält eine E-Mail mit Anmeldeinformationen</p>
                        </div>
                      </label>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Abbrechen
                    </Button>
                    <Button type="submit" disabled={createMutation.isPending} className="bg-primary hover:bg-primary/90">
                      {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Einladen
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {isLoading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : !users?.length ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Users className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Keine Benutzer vorhanden</p>
              </CardContent>
            </Card>
          ) : isMobile ? (
            // Mobile card view
            <div className="space-y-4">
              {users.map(u => {
                const primaryRole = getPrimaryRole(u.roles);
                const roleConfig = ROLE_CONFIG[primaryRole];
                const RoleIcon = roleConfig.icon;
                const userRolesList = parseRoles(u.roles);
                const displayRoles = getDisplayRoles(u.roles);
                const isCurrentUser = user?.id === u.id;
                
                return (
                  <Card key={u.id} className="overflow-hidden">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <Avatar className="h-10 w-10 border shrink-0">
                            <AvatarFallback className="text-sm font-medium bg-muted">
                              {u.name?.charAt(0).toUpperCase() || u.email?.charAt(0).toUpperCase() || '?'}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium truncate">{u.name || "Unbenannt"}</span>
                              {isCurrentUser && <Badge variant="outline" className="text-xs">Sie</Badge>}
                            </div>
                            <p className="text-sm text-muted-foreground truncate">{u.email}</p>
                            <div className="flex flex-wrap gap-1 mt-2">
                              {!u.isRegistered && (
                                <>
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${
                                    isInvitationExpired(u.invitedAt) 
                                      ? 'bg-red-100 text-red-700' 
                                      : 'bg-amber-100 text-amber-700'
                                  }`}>
                                    <Mail className={`h-3 w-3 ${
                                      isInvitationExpired(u.invitedAt) 
                                        ? 'text-red-600' 
                                        : 'text-amber-600'
                                    }`} />
                                    {isInvitationExpired(u.invitedAt) ? 'Einladung abgelaufen' : 'Einladung ausstehend'}
                                  </span>
                                  {u.invitedAt && (
                                    <span className="text-xs text-muted-foreground">
                                      {getInvitationAge(u.invitedAt)}
                                    </span>
                                  )}
                                </>
                              )}
                              {displayRoles.map(role => {
                                const config = ROLE_CONFIG[role];
                                const Icon = config.icon;
                                return (
                                  <span key={role} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${config.bgColor} ${config.textColor}`}>
                                    <Icon className={`h-3 w-3 ${config.iconColor}`} />
                                    {config.label}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {u.isRegistered ? (
                            <CheckCircle className="h-5 w-5 text-green-500" />
                          ) : (
                            <XCircle className="h-5 w-5 text-red-500" />
                          )}
                        </div>
                      </div>
                      
                      {canModifyUser(u.roles) && !isCurrentUser && (
                        <div className="mt-4 pt-4 border-t space-y-3">
                          {!u.isRegistered && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => resendInviteMutation.mutate({ id: u.id })}
                              disabled={resendInviteMutation.isPending}
                              className="w-full gap-2"
                            >
                              {resendInviteMutation.isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Mail className="h-4 w-4" />
                              )}
                              Einladung erneut senden
                            </Button>
                          )}
                          <div className="flex flex-wrap gap-2">
                            {(Object.keys(ROLE_CONFIG) as RoleKey[]).filter(r => r !== 'user').map(role => {
                              const config = ROLE_CONFIG[role];
                              const isActive = userRolesList.includes(role);
                              return (
                                <Button
                                  key={role}
                                  variant={isActive ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => handleRoleToggle(u.id, u.roles, role)}
                                  disabled={updateRolesMutation.isPending}
                                  className="text-xs"
                                >
                                  {config.label}
                                </Button>
                              );
                            })}
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={u.isRegistered}
                                onCheckedChange={(checked) => 
                                  toggleRegistrationMutation.mutate({ id: u.id, isRegistered: checked })
                                }
                              />
                              <span className="text-sm">{u.isRegistered ? "Aktiv" : "Deaktiviert"}</span>
                            </div>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="sm" className="text-destructive">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Benutzer löschen?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Möchten Sie {u.name || u.email} wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => deleteMutation.mutate({ id: u.id })}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  >
                                    Löschen
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            // Desktop table view
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Benutzer</TableHead>
                    <TableHead>Rollen</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Letzte Anmeldung</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map(u => {
                    const userRolesList = parseRoles(u.roles);
                    const displayRoles = getDisplayRoles(u.roles);
                    const isCurrentUser = user?.id === u.id;
                    
                    return (
                      <TableRow key={u.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-9 w-9">
                              <AvatarFallback className="bg-primary/10 text-primary text-sm">
                                {u.name?.charAt(0) || u.email?.charAt(0) || "?"}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{u.name || "Unbenannt"}</span>
                                {isCurrentUser && <Badge variant="outline" className="text-xs">Sie</Badge>}
                              </div>
                              <p className="text-sm text-muted-foreground">{u.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {!u.isRegistered && (
                              <>
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${
                                  isInvitationExpired(u.invitedAt) 
                                    ? 'bg-red-100 text-red-700' 
                                    : 'bg-amber-100 text-amber-700'
                                }`}>
                                  <Mail className={`h-3 w-3 ${
                                    isInvitationExpired(u.invitedAt) 
                                      ? 'text-red-600' 
                                      : 'text-amber-600'
                                  }`} />
                                  {isInvitationExpired(u.invitedAt) ? 'Einladung abgelaufen' : 'Einladung ausstehend'}
                                </span>
                                {u.invitedAt && (
                                  <span className="text-xs text-muted-foreground">
                                    {getInvitationAge(u.invitedAt)}
                                  </span>
                                )}
                              </>
                            )}
                            {displayRoles.map(role => {
                              const config = ROLE_CONFIG[role];
                              const Icon = config.icon;
                              return (
                                <span key={role} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${config.bgColor} ${config.textColor}`}>
                                  <Icon className={`h-3 w-3 ${config.iconColor}`} />
                                  {config.label}
                                </span>
                              );
                            })}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {u.isRegistered ? (
                              <>
                                <CheckCircle className="h-4 w-4 text-green-500" />
                                <span className="text-sm">Aktiv</span>
                              </>
                            ) : (
                              <>
                                <XCircle className="h-4 w-4 text-red-500" />
                                <span className="text-sm">Deaktiviert</span>
                              </>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {u.lastSignedIn 
                              ? format(new Date(u.lastSignedIn), "dd.MM.yyyy HH:mm", { locale: de })
                              : "Nie"
                            }
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {canModifyUser(u.roles) && !isCurrentUser ? (
                            <div className="flex items-center justify-end gap-2">
                              {!u.isRegistered && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => resendInviteMutation.mutate({ id: u.id })}
                                  disabled={resendInviteMutation.isPending}
                                  className="gap-2"
                                >
                                  {resendInviteMutation.isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Mail className="h-4 w-4" />
                                  )}
                                  Erneut senden
                                </Button>
                              )}
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button variant="outline" size="sm">
                                    Rollen bearbeiten
                                  </Button>
                                </DialogTrigger>
                                <DialogContent>
                                  <DialogHeader>
                                    <DialogTitle>Rollen für {u.name || u.email}</DialogTitle>
                                    <DialogDescription>
                                      Wählen Sie die Rollen für diesen Benutzer aus.
                                    </DialogDescription>
                                  </DialogHeader>
                                  <div className="space-y-2 py-4">
                                    {(Object.keys(ROLE_CONFIG) as RoleKey[]).filter(r => r !== 'user').map(role => {
                                      const config = ROLE_CONFIG[role];
                                      const Icon = config.icon;
                                      const isActive = userRolesList.includes(role);
                                      return (
                                        <div key={role} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-muted/50">
                                          <Checkbox
                                            id={`user-${u.id}-role-${role}`}
                                            checked={isActive}
                                            onCheckedChange={() => handleRoleToggle(u.id, u.roles, role)}
                                            disabled={updateRolesMutation.isPending}
                                          />
                                          <label htmlFor={`user-${u.id}-role-${role}`} className="flex items-center gap-2 cursor-pointer flex-1">
                                            <Icon className={`h-4 w-4 ${config.iconColor}`} />
                                            <div>
                                              <span className="font-medium">{config.label}</span>
                                              <p className="text-xs text-muted-foreground">{config.description}</p>
                                            </div>
                                          </label>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </DialogContent>
                              </Dialog>
                              
                              <Switch
                                checked={u.isRegistered}
                                onCheckedChange={(checked) => 
                                  toggleRegistrationMutation.mutate({ id: u.id, isRegistered: checked })
                                }
                              />
                              
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive">
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Benutzer löschen?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Möchten Sie {u.name || u.email} wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => deleteMutation.mutate({ id: u.id })}
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                      Löschen
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          )}
        </main>
      </SidebarInset>
    </>
  );
}
